import React, { useMemo, useState } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, TablePagination, Chip,
  IconButton, Checkbox, FormControlLabel, Popover
} from '@mui/material';
import { FileDownload, Settings, ChevronRight } from '@mui/icons-material';

// Bowler-chart colour palette
const COL_COLORS: Record<string, { bg: string; text: string }> = {
  new:            { bg: '#1565c0', text: '#fff' },
  cancelled:      { bg: '#6a1b9a', text: '#fff' },
  delayed:        { bg: '#b71c1c', text: '#fff' },
  delayedOverdue: { bg: '#e53935', text: '#fff' },
  decom:          { bg: '#2e7d32', text: '#fff' },
  tobe:           { bg: '#e65100', text: '#fff' },
};
const COL_LABELS: Record<string, string> = {
  new: 'New Additions',
  cancelled: 'Cancelled',
  delayed: 'Delayed',
  delayedOverdue: 'Delayed Overdue',
  decom: 'Decommissioned',
  tobe: 'To be Decommissioned',
};

interface Props {
  decommissioningData: any[];
}

// ─── constants ──────────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();
const SINCE_CURRENT_YEAR = new Date(`${CURRENT_YEAR}-01-01T00:00:00.000Z`);

// ─── helpers ───────────────────────────────────────────────────────────────────

const isTargetField = (fieldName: string) => {
  const f = (fieldName || '').toLowerCase();
  return f.includes('target') && f.includes('decommission') && f.includes('date')
    && !f.includes('reason') && !f.includes('extension');
};

const parseISODate = (v: any): Date | null => {
  if (!v || v === 'null') return null;
  const s = typeof v === 'string' ? v : (Array.isArray(v) ? v[0] : String(v));
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const parseItemDecom = (item: any): Date | null =>
  parseISODate(item.decom_date ?? item.decommissioned_date ?? item.decomDate ?? null);

/**
 * Reconstruct what the target decommissioning date was at the END of a given month,
 * by "undoing" any revisions that happened after monthEnd.
 * Sort revisions descending; for each with modifiedDate > monthEnd, set effectiveValue = oldValue.
 */
const getTargetAtMonthEnd = (item: any, monthEnd: Date): Date | null => {
  const targetRevs: { modifiedDate: Date; oldValue: any }[] = [];
  if (item.revisions && Array.isArray(item.revisions)) {
    for (const rev of item.revisions) {
      if (!rev.values || !Array.isArray(rev.values)) continue;
      for (const v of rev.values) {
        if (!isTargetField(v.fieldName || '')) continue;
        const d = parseISODate(rev.modifiedDate);
        if (d) targetRevs.push({ modifiedDate: d, oldValue: v.oldValue });
      }
    }
  }

  let effectiveStr: string | null = null;
  for (const f of ['targetDecommissioningDate', 'target_date_decom', 'targetDate', 'target_date']) {
    const val = item[f];
    if (val && val !== 'null') { effectiveStr = String(val); break; }
  }

  // Sort descending, undo any revision after monthEnd
  targetRevs.sort((a, b) => b.modifiedDate.getTime() - a.modifiedDate.getTime());
  for (const rev of targetRevs) {
    if (rev.modifiedDate > monthEnd) {
      const ov = rev.oldValue;
      effectiveStr = (ov === null || ov === undefined || ov === 'null') ? null : String(ov);
    }
  }

  return parseISODate(effectiveStr);
};

/**
 * Find if a TDD was newly set into the current year within [start,end].
 * Matches: null/blank → current-year date, OR prev-year date → current-year date.
 * Only considers revisions with modifiedDate >= since.
 */
const getAdditionInWindow = (
  item: any, start: Date, end: Date, since: Date
): { date: Date; modifiedBy: string | null; targetValue: string | null } | null => {
  if (!item.revisions || !Array.isArray(item.revisions)) return null;
  let best: { date: Date; modifiedBy: string | null; targetValue: string | null } | null = null;
  for (const rev of item.revisions) {
    if (!rev.values || !Array.isArray(rev.values)) continue;
    const revDate = parseISODate(rev.modifiedDate);
    if (!revDate || revDate < since || revDate < start || revDate > end) continue;
    for (const v of rev.values) {
      if (!isTargetField(v.fieldName || '')) continue;
      const parsedOld = parseISODate(v.oldValue);
      const parsedNew = parseISODate(v.newValue);
      const oldIsNullOrPrevYear = !parsedOld || parsedOld.getFullYear() < CURRENT_YEAR;
      const newIsCurrentYear = parsedNew && parsedNew.getFullYear() === CURRENT_YEAR;
      if (oldIsNullOrPrevYear && newIsCurrentYear) {
        if (!best || revDate > best.date) {
          const tv = v.newValue;
          best = {
            date: revDate,
            modifiedBy: rev.modifiedBy || null,
            targetValue: typeof tv === 'string' ? tv : (Array.isArray(tv) ? (tv[0] ?? null) : String(tv))
          };
        }
      }
    }
  }
  return best;
};

/**
 * Find if a target was cleared (set to blank/null) within [start,end].
 */
const getCancelledInWindow = (
  item: any, start: Date, end: Date
): { date: Date; modifiedBy: string | null; oldValue: string } | null => {
  if (!item.revisions || !Array.isArray(item.revisions)) return null;
  let best: { date: Date; modifiedBy: string | null; oldValue: string } | null = null;
  for (const rev of item.revisions) {
    if (!rev.values || !Array.isArray(rev.values)) continue;
    const revDate = parseISODate(rev.modifiedDate);
    if (!revDate || revDate < start || revDate > end) continue;
    for (const v of rev.values) {
      if (!isTargetField(v.fieldName || '')) continue;
      const parsedOld = parseISODate(v.oldValue);
      const newIsBlank = v.newValue === null || v.newValue === undefined || v.newValue === 'null' || v.newValue === '';
      // Only cancelled if the TDD being cleared was in the current year (app was in scope)
      if (parsedOld && parsedOld.getFullYear() === CURRENT_YEAR && newIsBlank) {
        if (!best || revDate > best.date) {
          best = { date: revDate, modifiedBy: rev.modifiedBy || null, oldValue: String(v.oldValue) };
        }
      }
    }
  }
  return best;
};

/**
 * Find if a TDD was moved from a current-year date to a different date within [start,end].
 * Old TDD must be in the current year (app was already in scope). New TDD is any valid date.
 * App must not be decommissioned.
 */
const getDelayedInWindow = (
  item: any, start: Date, end: Date
): { date: Date; modifiedBy: string | null; oldValue: string; newValue: string } | null => {
  if (!item.revisions || !Array.isArray(item.revisions)) return null;
  let best: { date: Date; modifiedBy: string | null; oldValue: string; newValue: string } | null = null;
  for (const rev of item.revisions) {
    if (!rev.values || !Array.isArray(rev.values)) continue;
    const revDate = parseISODate(rev.modifiedDate);
    if (!revDate || revDate < start || revDate > end) continue;
    for (const v of rev.values) {
      if (!isTargetField(v.fieldName || '')) continue;
      const parsedOld = parseISODate(v.oldValue);
      const parsedNew = parseISODate(v.newValue);
      // Only delayed if old TDD was already in current year and moved to a different date
      if (
        parsedOld && parsedOld.getFullYear() === CURRENT_YEAR &&
        parsedNew && parsedOld.getTime() !== parsedNew.getTime()
      ) {
        if (!best || revDate > best.date) {
          best = { date: revDate, modifiedBy: rev.modifiedBy || null, oldValue: String(v.oldValue), newValue: String(v.newValue) };
        }
      }
    }
  }
  return best;
};

// ─── component ─────────────────────────────────────────────────────────────────

const DecommissionTrends: React.FC<Props> = ({ decommissioningData = [] }) => {

  const getPortfolio = (item: any): string => {
    if (!item) return '';
    if (item.portfolio) return item.portfolio;
    if (item.portfolioName) return item.portfolioName;
    if (item.platform?.portfolio?.portfolioName) return item.platform.portfolio.portfolioName;
    if (item.platform?.portfolioName) return item.platform.portfolioName;
    if (item.platform?.platformName) return item.platform.platformName;
    return '';
  };

  // All months from Jan of current year up to and including today's month
  const allAvailableMonths = useMemo(() => {
    const now = new Date();
    const result: { key: string; label: string; start: Date; end: Date }[] = [];
    for (let m = 0; m <= now.getMonth(); m++) {
      const start = new Date(CURRENT_YEAR, m, 1);
      const end = new Date(CURRENT_YEAR, m + 1, 0, 23, 59, 59, 999);
      const label = `${start.toLocaleString('default', { month: 'short' })} ${CURRENT_YEAR}`;
      result.push({ key: `${CURRENT_YEAR}-${m}`, label, start, end });
    }
    return result;
  }, []);

  // Default: last 2 months
  const defaultKeys = useMemo(() => {
    const now = new Date();
    return allAvailableMonths
      .filter(m => m.start >= new Date(CURRENT_YEAR, now.getMonth() - 1, 1))
      .map(m => m.key);
  }, [allAvailableMonths]);

  const [selectedMonthKeys, setSelectedMonthKeys] = useState<string[]>(() => {
    const now = new Date();
    return allAvailableMonths
      .filter(m => m.start >= new Date(CURRENT_YEAR, now.getMonth() - 1, 1))
      .map(m => m.key);
  });

  // Months driven by selection, ordered chronologically
  const months = useMemo(() => {
    return allAvailableMonths.filter(m => selectedMonthKeys.includes(m.key));
  }, [allAvailableMonths, selectedMonthKeys]);

  const todayStart = useMemo(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  const portfolios = useMemo(() => {
    const set = new Set<string>();
    decommissioningData.forEach(item => {
      const p = getPortfolio(item);
      // exclude inactive/legacy portfolios
      if (p && !p.toLowerCase().startsWith('x_inactive') && !p.toLowerCase().startsWith('_inactive')) set.add(p);
    });
    return Array.from(set).sort();
  }, [decommissioningData]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRows, setDialogRows] = useState<any[]>([]);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogType, setDialogType] = useState<'new' | 'cancelled' | 'delayed' | 'delayedOverdue' | 'decom' | 'tobe'>('new');
  const [dialogPage, setDialogPage] = useState(0);
  const dialogRowsPerPage = 10;

  // Column visibility for the trends table
  const [visibleCols, setVisibleCols] = useState({
    new: true,
    cancelled: true,
    delayed: true,
    delayedOverdue: true,
    decom: true,
    tobe: true,
  });
  const [monthPickerAnchor, setMonthPickerAnchor] = useState<HTMLButtonElement | null>(null);
  const [yearExpanded, setYearExpanded] = useState(true);
  const [colSettingsAnchor, setColSettingsAnchor] = useState<HTMLButtonElement | null>(null);

  // ── aggregation ─────────────────────────────────────────────────────────────

  const tableRows = useMemo(() => {
    return portfolios.map(portfolio => {
      const row: any = { portfolio };
      months.forEach((_, mi) => {
        row[`M${mi + 1}_new`] = 0;
        row[`M${mi + 1}_cancelled`] = 0;
        row[`M${mi + 1}_delayed`] = 0;
        row[`M${mi + 1}_delayedOverdue`] = 0;
        row[`M${mi + 1}_decom`] = 0;
        row[`M${mi + 1}_tobe`] = 0;
      });

      decommissioningData
        .filter(it => getPortfolio(it) === portfolio)
        .forEach(item => {
          const isCancelled = (item.status || '').toLowerCase() === 'cancelled';
          const decom = parseItemDecom(item);

          months.forEach((m, mi) => {
            const { start, end } = m;
            // cutoff: for past months use end-of-month; for current/future month use today
            const cutoff = todayStart > end ? end : todayStart;
            // item was "open" during this month if not cancelled and not decommissioned by month end
            const openInMonth = !isCancelled && !(decom && decom <= end);

            // New Additions — only count current-year revisions where target went from null → value
            const addition = getAdditionInWindow(item, start, end, SINCE_CURRENT_YEAR);
            if (addition) {
              const addYear = parseISODate(addition.targetValue)?.getFullYear() ?? addition.date.getFullYear();
              if (addYear === CURRENT_YEAR) {
                const sameMonthCancelled = getCancelledInWindow(item, start, end);
                if (!sameMonthCancelled || sameMonthCancelled.date <= addition.date) {
                  row[`M${mi + 1}_new`] += 1;
                }
              }
            }

            // Cancelled — TDD was cleared (set to blank)
            if (getCancelledInWindow(item, start, end)) row[`M${mi + 1}_cancelled`] += 1;

            // Decommissioned in month
            if (decom && decom >= start && decom <= end) row[`M${mi + 1}_decom`] += 1;

            // Delayed — TDD was moved to a different date, app not decommissioned
            const delayed = getDelayedInWindow(item, start, end);
            if (delayed && !(decom && decom <= end)) row[`M${mi + 1}_delayed`] += 1;

            // Delayed Overdue — TDD not moved, in current year, has passed cutoff, app not decom'd
            if (openInMonth && !getDelayedInWindow(item, start, end)) {
              const histTarget = getTargetAtMonthEnd(item, end);
              if (histTarget && histTarget.getFullYear() === CURRENT_YEAR && histTarget <= cutoff) {
                row[`M${mi + 1}_delayedOverdue`] += 1;
              }
            }

            // To-be — open in month, histTarget falls within this specific month
            if (openInMonth) {
              const histTarget = getTargetAtMonthEnd(item, end);
              if (histTarget && histTarget >= start && histTarget <= end) {
                row[`M${mi + 1}_tobe`] += 1;
              }
            }
          });
        });

      return row;
    });
  }, [portfolios, months, decommissioningData, todayStart]);

  // ── totals row (sum of all portfolios) ───────────────────────────────────────
  const totalsRow = useMemo(() => {
    const total: any = { portfolio: 'Actual' };
    months.forEach((_, mi) => {
      (['new', 'cancelled', 'delayed', 'delayedOverdue', 'decom', 'tobe'] as const).forEach(key => {
        total[`M${mi + 1}_${key}`] = tableRows.reduce((sum, r) => sum + (r[`M${mi + 1}_${key}`] || 0), 0);
      });
    });
    return total;
  }, [tableRows, months]);

  // ── drilldown dialog ─────────────────────────────────────────────────────────

  const handleCountClick = (portfolio: string, monthIndex: number, type: 'new' | 'delayed' | 'delayedOverdue' | 'decom' | 'tobe' | 'cancelled') => {
    const m = months[monthIndex];
    const { start, end } = m;
    const cutoff = todayStart > end ? end : todayStart;
    const rows: any[] = [];

    decommissioningData
      .filter(it => getPortfolio(it) === portfolio)
      .forEach(item => {
        const isCancelled = (item.status || '').toLowerCase() === 'cancelled';
        const decom = parseItemDecom(item);
        const openInMonth = !isCancelled && !(decom && decom <= end);

        if (type === 'new') {
          const addition = getAdditionInWindow(item, start, end, SINCE_CURRENT_YEAR);
          if (addition) {
            const addYear = parseISODate(addition.targetValue)?.getFullYear() ?? addition.date.getFullYear();
            if (addYear === CURRENT_YEAR) {
              const sameMonthCancelled = getCancelledInWindow(item, start, end);
              if (!sameMonthCancelled || sameMonthCancelled.date <= addition.date) {
                const tvDate = parseISODate(addition.targetValue);
                rows.push({
                  app_id: item.app_id, app_name: item.app_name,
                  target_date: tvDate ? tvDate.toLocaleDateString() : (addition.targetValue || ''),
                  modifiedDate: addition.date.toLocaleString(), modifiedBy: addition.modifiedBy,
                  mvc_group: item.mvc_group || ''
                });
              }
            }
          }

        } else if (type === 'cancelled') {
          const cancelled = getCancelledInWindow(item, start, end);
          if (cancelled) {
            rows.push({
              app_id: item.app_id, app_name: item.app_name,
              old_target: parseISODate(cancelled.oldValue)?.toLocaleDateString() ?? cancelled.oldValue,
              modifiedDate: cancelled.date.toLocaleString(), modifiedBy: cancelled.modifiedBy,
              mvc_group: item.mvc_group || ''
            });
          }

        } else if (type === 'decom') {
          if (decom && decom >= start && decom <= end) {
            const histTarget = getTargetAtMonthEnd(item, end);
            rows.push({
              app_id: item.app_id, app_name: item.app_name,
              target_date: histTarget ? histTarget.toLocaleDateString() : 'N/A',
              decom_date: decom.toLocaleDateString(),
              mvc_group: item.mvc_group || ''
            });
          }

        } else if (type === 'delayed') {
          const delayed = getDelayedInWindow(item, start, end);
          if (delayed && !(decom && decom <= end)) {
            rows.push({
              app_id: item.app_id, app_name: item.app_name,
              old_target: parseISODate(delayed.oldValue)?.toLocaleDateString() ?? delayed.oldValue,
              new_target: parseISODate(delayed.newValue)?.toLocaleDateString() ?? delayed.newValue,
              modifiedDate: delayed.date.toLocaleString(), modifiedBy: delayed.modifiedBy,
              mvc_group: item.mvc_group || ''
            });
          }

        } else if (type === 'tobe') {
          if (openInMonth) {
            const histTarget = getTargetAtMonthEnd(item, end);
            if (histTarget && histTarget >= start && histTarget <= end) {
              rows.push({
                app_id: item.app_id, app_name: item.app_name,
                target_date: histTarget.toLocaleDateString(),
                mvc_group: item.mvc_group || ''
              });
            }
          }

        } else if (type === 'delayedOverdue') {
          if (openInMonth && !getDelayedInWindow(item, start, end)) {
            const histTarget = getTargetAtMonthEnd(item, end);
            if (histTarget && histTarget.getFullYear() === CURRENT_YEAR && histTarget <= cutoff) {
              rows.push({
                app_id: item.app_id, app_name: item.app_name,
                target_date: histTarget.toLocaleDateString(),
                mvc_group: item.mvc_group || ''
              });
            }
          }
        }
      });

    const titleMap: Record<string, string> = {
      new: 'New Additions', cancelled: 'Cancelled',
      delayed: 'Delayed', delayedOverdue: 'Delayed Overdue',
      decom: 'Decommissioned', tobe: 'To be Decommissioned'
    };
    setDialogRows(rows);
    setDialogTitle(`${portfolio} — ${titleMap[type]} (${m.label})`);
    setDialogType(type);
    setDialogPage(0);
    setDialogOpen(true);
  };

  // ── CSV export ───────────────────────────────────────────────────────────────

  const handleExportCSV = () => {
    const headers = ['Portfolio',
      ...months.flatMap(m => [
        `${m.label} - New Additions`, `${m.label} - Cancelled`,
        `${m.label} - Delayed`, `${m.label} - Delayed Overdue`,
        `${m.label} - Decommissioned`, `${m.label} - To be Decommissioned`
      ])
    ];
    const lines = [headers.join(','),
      ...tableRows.map(r => [r.portfolio,
        ...months.flatMap((_, mi) => [
          r[`M${mi + 1}_new`], r[`M${mi + 1}_cancelled`],
          r[`M${mi + 1}_delayed`], r[`M${mi + 1}_delayedOverdue`],
          r[`M${mi + 1}_decom`], r[`M${mi + 1}_tobe`]
        ])
      ].join(','))
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'decommission_trends.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── render ───────────────────────────────────────────────────────────────────

  const COLS = (['new', 'cancelled', 'delayed', 'delayedOverdue', 'decom', 'tobe'] as const).filter(
    k => visibleCols[k]
  );

  return (
    <Box>
      {/* Header bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Decommission Trends (Monthly)</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Month selector button */}
          <Button
            onClick={(e) => setMonthPickerAnchor(e.currentTarget)}
            size="small"
            variant="outlined"
            sx={{
              height: 34, fontSize: '0.82rem', textTransform: 'none',
              borderColor: '#ccc', color: '#333', minWidth: 160,
              justifyContent: 'space-between',
              '&:hover': { borderColor: '#999', bgcolor: '#f9f9f9' }
            }}
            endIcon={<ChevronRight sx={{ fontSize: 16 }} />}
          >
            {selectedMonthKeys.length === allAvailableMonths.length
              ? `All months`
              : selectedMonthKeys.length === 0
                ? 'No months selected'
                : selectedMonthKeys.length === 1
                  ? allAvailableMonths.find(m => m.key === selectedMonthKeys[0])?.label
                  : `${selectedMonthKeys.length} months selected`}
          </Button>
          <IconButton
            onClick={(e) => setColSettingsAnchor(e.currentTarget)}
            size="small"
            title="Column Settings"
            sx={{ bgcolor: '#f5f5f5', border: '1px solid #ccc', borderRadius: 1, '&:hover': { bgcolor: '#e0e0e0' } }}
          >
            <Settings sx={{ fontSize: '17px', color: '#555' }} />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={handleExportCSV}
            size="small"
            sx={{ bgcolor: '#1565c0', '&:hover': { bgcolor: '#003d82' } }}
          >Export CSV</Button>
        </Box>
      </Box>

      {/* Bowler chart table */}
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, overflow: 'auto' }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { border: '1px solid #ccc' } }}>
          <TableHead>
            {/* Month header row */}
            <TableRow sx={{ bgcolor: '#003d82' }}>
              <TableCell
                rowSpan={2}
                sx={{ color: '#fff', fontWeight: 700, minWidth: 160, fontSize: '0.8rem', border: '1px solid #1565c0', verticalAlign: 'middle' }}
              >
                Portfolio
              </TableCell>
              {months.map(m => (
                <TableCell
                  key={m.label}
                  align="center"
                  colSpan={Math.max(1, COLS.length)}
                  sx={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem', border: '1px solid #1565c0', letterSpacing: 0.5 }}
                >
                  {m.label}
                </TableCell>
              ))}
            </TableRow>
            {/* Metric sub-header row */}
            <TableRow>
              {months.map((_, idx) => (
                <React.Fragment key={`h-${idx}`}>
                  {COLS.map(key => (
                    <TableCell
                      key={key}
                      align="center"
                      sx={{
                        bgcolor: COL_COLORS[key].bg,
                        color: COL_COLORS[key].text,
                        fontWeight: 600,
                        fontSize: '0.72rem',
                        whiteSpace: 'nowrap',
                        border: '1px solid rgba(255,255,255,0.25)',
                        py: 0.75,
                        minWidth: 80,
                      }}
                    >
                      {COL_LABELS[key]}
                    </TableCell>
                  ))}
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length === 0
              ? <TableRow><TableCell colSpan={1 + months.length * Math.max(1, COLS.length)} align="center">No data</TableCell></TableRow>
              : tableRows.map((r, ri) => (
                <TableRow key={r.portfolio} hover sx={{ bgcolor: ri % 2 === 0 ? '#fafafa' : '#fff' }}>
                  <TableCell sx={{ fontWeight: 500, fontSize: '0.78rem' }}>{r.portfolio || 'Unknown'}</TableCell>
                  {months.map((_, mi) => (
                    <React.Fragment key={`${r.portfolio}-${mi}`}>
                      {COLS.map(key => {
                        const count = r[`M${mi + 1}_${key}`];
                        const clickType = key as any;
                        return (
                          <TableCell key={key} align="center" sx={{ p: 0.5 }}>
                            {count > 0
                              ? (
                                <Chip
                                  label={count}
                                  size="small"
                                  onClick={() => handleCountClick(r.portfolio, mi, clickType)}
                                  sx={{
                                    bgcolor: COL_COLORS[key].bg,
                                    color: COL_COLORS[key].text,
                                    fontWeight: 700,
                                    fontSize: '0.78rem',
                                    cursor: 'pointer',
                                    height: 24,
                                    minWidth: 32,
                                    '& .MuiChip-label': { px: 1 },
                                    '&:hover': { opacity: 0.85 },
                                  }}
                                />
                              )
                              : <Typography variant="caption" sx={{ color: '#bbb' }}>0</Typography>}
                          </TableCell>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </TableRow>
              ))
            }
            {/* Actual / totals row */}
            {tableRows.length > 0 && (
              <TableRow sx={{ bgcolor: '#e8edf2', borderTop: '2px solid #003d82' }}>
                <TableCell sx={{ color: '#111', fontWeight: 700, fontSize: '0.8rem' }}>Actual</TableCell>
                {months.map((_, mi) => (
                  <React.Fragment key={`total-${mi}`}>
                    {COLS.map(key => {
                      const count = totalsRow[`M${mi + 1}_${key}`];
                      return (
                        <TableCell key={key} align="center" sx={{ p: 0.5 }}>
                          <Typography sx={{ color: '#111', fontWeight: 700, fontSize: '0.82rem' }}>
                            {count}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </React.Fragment>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Drilldown dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ bgcolor: '#003d82', color: '#fff', fontWeight: 700 }}>{dialogTitle}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { border: '1px solid #e0e0e0' } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#003d82' }}>
                  {(() => {
                    const baseCell = (h: string) => (
                      <TableCell key={h} sx={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{h}</TableCell>
                    );
                    if (dialogType === 'new') return ['App ID', 'App Name', 'Target Decom Date', 'Modified Date', 'Modified By', 'MVC Group'].map(baseCell);
                    if (dialogType === 'cancelled') return ['App ID', 'App Name', 'TDD (Cleared)', 'Modified Date', 'Modified By', 'MVC Group'].map(baseCell);
                    if (dialogType === 'delayed') return ['App ID', 'App Name', 'Old TDD', 'New TDD', 'Modified Date', 'Modified By', 'MVC Group'].map(baseCell);
                    if (dialogType === 'delayedOverdue') return ['App ID', 'App Name', 'TDD', 'MVC Group'].map(baseCell);
                    if (dialogType === 'decom') return ['App ID', 'App Name', 'Target Decom Date', 'Decom Date', 'MVC Group'].map(baseCell);
                    /* tobe */ return ['App ID', 'App Name', 'Target Decom Date', 'MVC Group'].map(baseCell);
                  })()}
                </TableRow>
              </TableHead>
              <TableBody>
                {dialogRows
                  .slice(dialogPage * dialogRowsPerPage, (dialogPage + 1) * dialogRowsPerPage)
                  .map((row, idx) => (
                    <TableRow key={idx} hover sx={{ bgcolor: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                      <TableCell sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                        {row.app_id ? (
                          <a
                            href={`https://mac.maersk.io/application/${row.app_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1565c0', textDecoration: 'underline', fontWeight: 600 }}
                          >
                            {row.app_id}
                          </a>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{row.app_name || ''}</TableCell>
                      {dialogType === 'cancelled' ? (
                        <>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.old_target || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.modifiedDate || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.modifiedBy || 'N/A'}</TableCell>
                        </>
                      ) : dialogType === 'decom' ? (
                        <>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.target_date || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.decom_date || 'N/A'}</TableCell>
                        </>
                      ) : dialogType === 'tobe' ? (
                        <TableCell sx={{ fontSize: '0.78rem' }}>{row.target_date || 'N/A'}</TableCell>
                      ) : dialogType === 'delayed' ? (
                        <>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.old_target || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.new_target || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.modifiedDate || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.modifiedBy || 'N/A'}</TableCell>
                        </>
                      ) : dialogType === 'delayedOverdue' ? (
                        <TableCell sx={{ fontSize: '0.78rem' }}>{row.target_date || 'N/A'}</TableCell>
                      ) : /* new */ (
                        <>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.target_date || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.modifiedDate || 'N/A'}</TableCell>
                          <TableCell sx={{ fontSize: '0.78rem' }}>{row.modifiedBy || 'N/A'}</TableCell>
                        </>
                      )}
                      <TableCell sx={{ fontSize: '0.78rem' }}>{row.mvc_group || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" size="small">Close</Button>
          <TablePagination
            component="div"
            count={dialogRows.length}
            page={dialogPage}
            onPageChange={(_, p) => setDialogPage(p)}
            rowsPerPage={dialogRowsPerPage}
            rowsPerPageOptions={[dialogRowsPerPage]}
            labelRowsPerPage=""
          />
        </DialogActions>
      </Dialog>

      {/* Month Picker Popover */}
      <Popover
        open={Boolean(monthPickerAnchor)}
        anchorEl={monthPickerAnchor}
        onClose={() => setMonthPickerAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: 1, boxShadow: 4, minWidth: 220 } }}
      >
        <Box>
          {/* Header */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 1.5, py: 1, bgcolor: '#e8eef6', borderBottom: '1px solid #d0d9e8'
          }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a3a5c', letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Year / Period
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Typography
                component="span"
                onClick={() => setSelectedMonthKeys(allAvailableMonths.map(m => m.key))}
                sx={{ fontSize: '0.75rem', color: '#1565c0', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >All</Typography>
              <Typography
                component="span"
                onClick={() => setSelectedMonthKeys([])}
                sx={{ fontSize: '0.75rem', color: '#1565c0', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
              >Clear</Typography>
            </Box>
          </Box>
          {/* Year row */}
          <Box
            onClick={() => {
              // Toggle expand on chevron side; toggle all selection on checkbox side handled below
              setYearExpanded(p => !p);
            }}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 1.5, py: 0.75, cursor: 'pointer', bgcolor: '#f0f4fa',
              borderBottom: '1px solid #e0e8f0',
              '&:hover': { bgcolor: '#e3eaf5' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                size="small"
                checked={selectedMonthKeys.length === allAvailableMonths.length}
                indeterminate={selectedMonthKeys.length > 0 && selectedMonthKeys.length < allAvailableMonths.length}
                onChange={(e) => {
                  e.stopPropagation();
                  setSelectedMonthKeys(e.target.checked ? allAvailableMonths.map(m => m.key) : []);
                }}
                onClick={(e) => e.stopPropagation()}
                sx={{ p: 0 }}
              />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a3a5c' }}>{CURRENT_YEAR}</Typography>
            </Box>
            <ChevronRight sx={{ fontSize: 16, color: '#666', transform: yearExpanded ? 'rotate(90deg)' : 'none', transition: '0.2s' }} />
          </Box>
          {/* Month rows */}
          {yearExpanded && allAvailableMonths.map(m => {
            const selected = selectedMonthKeys.includes(m.key);
            return (
              <Box
                key={m.key}
                onClick={() => {
                  setSelectedMonthKeys(prev =>
                    selected ? prev.filter(k => k !== m.key) : [...prev, m.key]
                  );
                }}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1.5, py: 0.6, cursor: 'pointer',
                  bgcolor: selected ? '#dce8f8' : '#fff',
                  borderBottom: '1px solid #f0f0f0',
                  '&:hover': { bgcolor: selected ? '#ccdff5' : '#f5f8ff' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    size="small"
                    checked={selected}
                    onChange={() => {}}
                    sx={{ p: 0, pointerEvents: 'none' }}
                  />
                  <Typography sx={{ fontSize: '0.82rem', color: '#1a3a5c', fontWeight: selected ? 600 : 400 }}>
                    {m.label.split(' ')[0]}
                  </Typography>
                </Box>
                <ChevronRight sx={{ fontSize: 14, color: '#aaa' }} />
              </Box>
            );
          })}
        </Box>
      </Popover>

      {/* Column Settings Popover */}
      <Popover
        open={Boolean(colSettingsAnchor)}
        anchorEl={colSettingsAnchor}
        onClose={() => setColSettingsAnchor(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: 4 } }}
      >
        <Box sx={{ minWidth: 320, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{
            display: 'flex', alignItems: 'center',
            px: 3, py: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f7fa'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Settings sx={{ fontSize: 18, color: '#555' }} /> Column Settings
            </Typography>
          </Box>
          {/* Body */}
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Toggle metric columns:
            </Typography>
            {(['new', 'cancelled', 'delayed', 'delayedOverdue', 'decom', 'tobe'] as const).map(key => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={visibleCols[key]}
                    onChange={(e) => setVisibleCols(prev => ({ ...prev, [key]: e.target.checked }))}
                    sx={{ py: 0.75 }}
                  />
                }
                label={<Typography variant="body1">{COL_LABELS[key]}</Typography>}
                sx={{ display: 'flex', m: 0, mb: 0.75 }}
              />
            ))}
          </Box>
          {/* Footer with Close button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1.5, borderTop: '1px solid #e0e0e0' }}>
            <Button onClick={() => setColSettingsAnchor(null)} sx={{ color: '#1565c0', fontWeight: 600, textTransform: 'none' }}>
              Close
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default DecommissionTrends;
